import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'
import { lastExpectedBalanceDate } from '@/lib/date-utils'
import type { CashPositionFreshness } from '@/components/banking/cashPositionWarnings'

/**
 * Per-facility "is today's data actually there?" signals for the Cash Position
 * Summary report (DCP-161).
 *
 * The report itself comes from one RPC that has no notion of freshness — it
 * `COALESCE`s a missing bank or Intacct balance to 0, so an un-entered figure is
 * indistinguishable from a real zero. This hook supplies the counts the grid needs
 * to flag those cells, and `buildCashPositionWarnings` turns them into sentences.
 *
 * Two properties make it work:
 *
 *  - **The query key is nested under {@link CASH_POSITION_SUMMARIES_KEY}.** Every
 *    mutation that can FIX missing data — a manual balance entry, a Sage balance
 *    sync, bank-account CRUD, a file import — already calls
 *    `invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })` without
 *    `exact`, and React Query matches keys by prefix. So the warnings clear on the
 *    same round trip as the figures, with no new invalidation wiring (AC#3).
 *  - **It is NOT filter-scoped.** The maps cover every facility in the org, and the
 *    grid picks per column. Changing the pack/facility filter therefore never
 *    refetches this (no icon flicker over the summary's `placeholderData`), and no
 *    per-column N+1 is possible: the request count is a function of how many rows
 *    the org holds — the two calls below page their reads to survive PostgREST's
 *    1000-row cap — never of how many columns are on screen.
 *
 * Each underlying call degrades to `null` on failure, exactly like `useBankingKpi`
 * does with its own freshness signals: a signal we could not load must render as NO
 * warning, never as a missing-data warning.
 *
 * @returns the freshness maps plus the business date they were measured against
 */
export function useCashPositionFreshness() {
  const { currentOrganization } = useOrganization()
  // The last business day on the BUSINESS calendar (`businessDate`, @dcp/shared)
  // — the same calendar `balances.date` is written with, and the same one the
  // report's period ladder and "As of" header are anchored to, so a warning can
  // never point at a different day than the figures beside it. It is part of the
  // query key, so once the page re-renders on or after the next business midnight
  // the key changes and the data is refetched for the new day. (It is NOT a timer:
  // a tab left untouched overnight keeps yesterday's key until something
  // re-renders it.)
  const targetDate = lastExpectedBalanceDate()

  return useQuery<CashPositionFreshness>({
    queryKey: [...CASH_POSITION_SUMMARIES_KEY, 'freshness', currentOrganization?.id, targetDate],
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)

      const [coverageResult, intacctResult] = await Promise.all([
        tenantDb.bankBalance.getCoverageByFacility({ date: targetDate }).catch((err) => {
          console.error('[useCashPositionFreshness] getCoverageByFacility threw:', err)
          return null
        }),
        tenantDb.sageBankAccounts.getIntacctFreshnessByFacility({ activeOnly: true }).catch((err) => {
          console.error('[useCashPositionFreshness] getIntacctFreshnessByFacility threw:', err)
          return null
        }),
      ])

      if (coverageResult?.error) {
        console.error(
          '[useCashPositionFreshness] getCoverageByFacility error:',
          coverageResult.error.message,
        )
      }
      if (intacctResult?.error) {
        console.error(
          '[useCashPositionFreshness] getIntacctFreshnessByFacility error:',
          intacctResult.error.message,
        )
      }

      const coverageOk = coverageResult != null && !coverageResult.error
      const intacctOk = intacctResult != null && !intacctResult.error

      return {
        targetDate,
        bankByFacility: coverageOk ? coverageResult.data.byFacility : {},
        intacctByFacility: intacctOk ? intacctResult.data.byFacility : {},
        partial: !coverageOk || !intacctOk,
      }
    },
    enabled: !!currentOrganization?.id,
    // A degraded result resolves rather than throws, so React Query would store
    // it as a plain success and serve it for the whole default stale window
    // (5 min, queryClient.ts) with no retry — meaning one transient failure
    // silently strips every warning off the report while the figures keep
    // rendering. Marking such a result immediately stale makes the next mount
    // try again; a complete result keeps the normal window.
    staleTime: (query) => (query.state.data?.partial ? 0 : 5 * 60 * 1000),
    // No placeholderData on purpose: while an org switch is in flight the grid keeps
    // the previous org's figures (guarded to the same org id), but warnings must NOT
    // carry over — an absent signal shows nothing, which fails in the safe direction.
  })
}
